import React, { useCallback, useEffect, useState } from "react";
import Layout from "../../layout/Layout";
import { Form, InputGroup, OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import CustomPagination from "../../components/CustomPagination";
import { Link } from "react-router-dom";
import DeleteModal from "../../components/DeleteModal";
import ViewUser from "../../components/user/ViewUser";
import NoDateFound from "../../components/NoDateFound";
import Loader from "../../components/loader/Loader";
import { useGlobalState } from "../../context/GlobalStateContext";
import axiosInstance from "../../utils/axiosConfig";
import * as CustomToast from "../../components/CustomToastMessage";
import { DEVICE_TYPES } from "../../common/constants";

function UserList() {
  const [users, setUsers] = useState([]);
  const {
    loading,
    setLoading,
    search,
    setSearch,
    pagination,
    setPagination,
    handleLimitChange,
  } = useGlobalState();
  const [sortBy, setSortBy] = useState("");
  const [isDeletedFilter, setIsDeletedFilter] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState(search);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);
  const fetchUsers = useCallback(
    async (page = 1, limit = 10, searchQuery = "", sort = "", isDeleted = "") => {
      try {
        setLoading(true);
        const params = {
          page,
          limit,
          search: searchQuery,
          sort: sort,
        };
        if (isDeleted !== "") {
          params.isDeleted = Number(isDeleted);
        }
        const response = await axiosInstance.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/users/list`,
          { params }
        );
        if (response.data.success) {
          setUsers(response.data.data);
          setPagination(response.data.pagination);
        } else {
          setUsers([]);
          setPagination((prev) => ({
            ...prev,
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
          }));
        }
      } catch (error) {
        setUsers([]);
        setPagination((prev) => ({
          ...prev,
          currentPage: 1,
          totalPages: 1,
          totalItems: 0,
        }));
        if (error.response.data.status === 400) {
          CustomToast.error(error.response?.data?.message);
        }
        console.error(error.response?.data?.message || "Internal server error");
      } finally {
        setLoading(false);
      }
    },
    [setLoading, setPagination]
  );
  const handleDelete = async (id) => {
    try {
      setLoading(true);

      const response = await axiosInstance.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/user/delete/${id}`
      );
      if (response.data.success) {
        CustomToast.success(response.data.message);
        fetchUsers(pagination.currentPage, pagination.limit, search, sortBy, isDeletedFilter);
      }
    } catch (error) {
      if (error.response.data.status === 400) {
        CustomToast.error(error.response?.data?.message);
      }
      console.error(error.response?.data?.message || "Internal server error");
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (e) => {
    const value = e.target.value;
    setSortBy(value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleIsDeletedChange = (e) => {
    const value = e.target.value;
    setIsDeletedFilter(value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  useEffect(() => {
    fetchUsers(
      pagination.currentPage,
      pagination.limit,
      debouncedSearch,
      sortBy,
      isDeletedFilter
    );
  }, [
    pagination.currentPage,
    pagination.limit,
    debouncedSearch,
    sortBy,
    isDeletedFilter,
    fetchUsers,
  ]);

  const handleExportCSV = async () => {
    try {
      const response = await axiosInstance({
        url: `${process.env.REACT_APP_BACKEND_URL}/api/users/export`,
        method: "GET",
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "text/csv" });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `users_export_${new Date().toISOString().split("T")[0]}.csv`
      );
      document.body.appendChild(link);
      link.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(link);
    } catch (error) {
      CustomToast.error("Failed to export users data");
      console.error("Export error:", error);
    }
  };
  const handleUserStatusChange = async (id, currentStatus) => {
    try {
      const response = await axiosInstance.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/user/status/${id}`,
        {
          isActive: !currentStatus,
        }
      );
      if (response.data.success) {
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user._id === id ? { ...user, isActive: !currentStatus } : user
          )
        );
        CustomToast.success(response.data.message);
      }
    } catch (error) {
      CustomToast.error(
        error.response?.data?.message || "Failed to update trending status"
      );
    }
  };

  const getDeviceTypeLabel = (deviceType) => {
    if (!deviceType) return "-";
    // handle numeric constants from DEVICE_TYPES or string values
    switch (deviceType) {
      case DEVICE_TYPES.ANDROID:
        return "Android";
      case DEVICE_TYPES.IOS:
        return "IOS";
      case DEVICE_TYPES.WEB:
        return "Website";
      default:
        return "-";
    }
  };

  return (
    <Layout title="Users List">
      <main>
        <div className="card page-card">
          <div className="card-header d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
            <div className="d-flex align-items-center gap-3">
              <div className="d-flex align-items-center gap-3">
                <p>Show</p>
                <Form.Select
                  aria-label="Default select example"
                  value={pagination.limit}
                  onChange={handleLimitChange}
                >
                  <option value="10">10</option>
                  <option value="25">25</option>
                  <option value="50">50</option>
                </Form.Select>
                <p>entries</p>
              </div>

              <div className="d-flex align-items-center gap-3 ms-3">
                <p>Sort by</p>
                <Form.Select
                  aria-label="Sort options"
                  value={sortBy}
                  onChange={handleSortChange}
                  style={{ minWidth: "180px" }}
                >
                  <option value="email_asc">Email (A-Z)</option>
                  <option value="email_desc">Email (Z-A)</option>
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                </Form.Select>
              </div>

              <div className="d-flex align-items-center gap-3 ms-3">
                <p>User status</p>
                
                <Form.Select
                  aria-label="Deleted filter"
                  value={isDeletedFilter}
                  onChange={handleIsDeletedChange}
                  style={{ minWidth: "160px" }}
                >
                  <option value="">All users</option>
                  <option value="0">Active users</option>
                  <option value="1">Deleted users</option>
                </Form.Select>
              </div>
            </div>

            <div className="d-flex flex-column flex-md-row  align-items-start align-items-md-center gap-3 sm-w-100">
              <div className="header-search">
                <InputGroup>
                  <InputGroup.Text id="basic-addon1" className="bg-transparent">
                    <img src="/images/png/search.png" width={20} alt="" />
                  </InputGroup.Text>
                  <Form.Control
                    type="search"
                    className="border-start-0 ps-0"
                    placeholder="search here..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPagination((prev) => ({ ...prev, currentPage: 1 }));
                    }}
                  />
                </InputGroup>
              </div>
              <Link
                onClick={handleExportCSV}
                className="btn-outline fs-16 fw-600 d-flex justify-content-center align-items-center gap-2 px-3 edit-hover"
              >
                <img src="/images/png/upload-2.png" width={22} alt="" />
                Export to csv
              </Link>
            </div>
          </div>
          <div className="card-body">
            <div className="overflow-auto">
              <Table className="vertical-align">
                <thead>
                  <tr>
                    <th>Id</th>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Login Type</th>
                    {/* <th>Phone</th>
                                                                            <th>ZIP Code</th>
                                                                            <th>Birthdate</th> */}
                    <th>Subscribed</th>
                    <th>Completed Tips</th>
                    <th>Device Type</th>
                    <th>Created Date</th>

                    <th>Status</th>

                    <th width="100" className="text-center">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="text-center">
                        <Loader />
                      </td>
                    </tr>
                  ) : users.length > 0 ? (
                    users.map((user, index) => (
                      <tr key={user._id}>
                        <td>
                          <p className="mb-0">
                            {(pagination.currentPage - 1) * pagination.limit +
                              index +
                              1}
                          </p>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                          {(user.isDeleted === 1 || user.isDeleted === true) && (
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id={`deleted-user-${user._id}`}>
                                    Deleted user
                                  </Tooltip>
                                }
                              >
                                <img
                                  src="/images/png/user-delete.png"
                                  width={18}
                                  alt="Deleted user"
                                />
                              </OverlayTrigger>
                            )}
                            <span>{user.email ? user.email : "-"}</span>
                         
                          </div>
                        </td>
                        <td className="table-td-200">
                          {user.firstName
                            ? user.lastName
                              ? user.firstName + " " + user.lastName
                              : user.firstName
                            : "-"}
                        </td>
                        <td>
                          {user.socialLoginType === 0
                            ? "Email"
                            : user.socialLoginType === 2
                            ? "Google"
                            : user.socialLoginType === 1
                            ? "Facebook"
                            : user.socialLoginType === 3
                            ? "Apple"
                            : "-"}
                        </td>
                        {/* <td className='table-td-130'>{user.contactNo ? user.contactNo : "-"}</td> */}
                        {/* <td className='table-td-130'>{user.zipCode ? user.zipCode : "-"}</td> */}
                        {/* <td className='table-td-130'>{user.dob ? new Date(user.dob).toISOString().split('T')[0] : "-"}</td> */}
                        <td className="table-td-130">
                          {user.subscription === true ? "true" : "false"}
                        </td>
                        <td className="table-td-130">
                          {user.tipsCompleted || 0}
                        </td>
                        <td className="table-td-130">
                          {getDeviceTypeLabel(user?.deviceType)}
                        </td>
                        <td className="table-td-130">
                          {user.createdAt
                            ? new Date(user.createdAt)
                                .toISOString()
                                .split("T")[0]
                            : "-"}
                        </td>

                        <td>
                          
                          <Form.Check
                            type="switch"
                            id={`trending-switch-${user._id}`}
                            className="custom-switch"
                            checked={user.isActive || false}
                            onChange={() =>
                              handleUserStatusChange(user._id, user.isActive)
                            }
                            disabled={loading || user.isDeleted === 1}
                            style={{ cursor: user.isDeleted === 1 ? "not-allowed" : "pointer" }}
                          />
                        </td>

                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <ViewUser user={user} />
                            {user.isDeleted === 0 && (
                            <DeleteModal
                              onConfirm={() => handleDelete(user._id)}
                              loading={loading}
                                flag={"user"}
                              />
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center">
                        <NoDateFound />
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
            <CustomPagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              limit={pagination.limit}
              onPageChange={(page) =>
                setPagination((prev) => ({ ...prev, currentPage: page }))
              }
            />
          </div>
        </div>
      </main>
    </Layout>
  );
}

export default UserList;
